<template>
  <section>
    <div class="titulo">
      <div class="margem container">
        <h2>Indicadores de inspeção de máquina</h2>
      </div>
    </div>
    <div style="height: 20px"></div>
    <div class="margem">
      <div class="margem bloco container">
        <div class="coluna alinha-centro alinha-v">
          <div>
            <h3 class="alinha-centro">Intervalo de busca</h3>
            <div class="linha alinha-v">
              <div>De</div>
              <input type="date" v-model="startDt" />
              até
              <input type="date" v-model="endDt" />
            </div>
          </div>
        </div>
        <button class="search-btn" @click="filter">Filtrar</button>
      </div>
    </div>

    <div class="margem">
      <div class="grid-2 container">
        <div class="bloco">
          <div class="fonte-fraca">Total de inspeçoes</div>
          <div v-if="isLoading" class="loading"><div></div></div>
          <h3 v-else>{{ report.totalInspections }}</h3>
        </div>
        <div class="bloco margem">
          <div class="fonte-fraca">Inconformidades detectadas</div>
          <div v-if="isLoading" class="loading"><div></div></div>
          <h3 v-else>{{ report.totalNonConformities }}</h3>
        </div>
      </div>
    </div>

    <div class="toggle-mode-container">
      <button
        :class="{
          'search-btn': mode === 'chart',
          'acao-secundaria': mode !== 'chart',
        }"
        @click="changeMode('chart')"
      >
        Gráfico
      </button>
      <button
        :class="{
          'search-btn': mode === 'table',
          'acao-secundaria': mode !== 'table',
        }"
        @click="changeMode('table')"
      >
        Tabela
      </button>
    </div>

    <div class="margem container grid-2" v-if="mode === 'chart'">
      <div class="bloco">
        <h3 class="card-title">Inspeções por máquina</h3>
        <div v-if="isLoading" class="loading"><div></div></div>
        <div v-else id="chart-wrapper">
          <BarChart :chart-data-prop="inspectionsPerMachine" :options="chartOptions" />
        </div>
      </div>
      <div class="bloco">
        <h3 class="card-title">Inconformidades por máquina</h3>
        <div v-if="isLoading" class="loading"><div></div></div>
        <div v-if="!isLoading" id="chart-wrapper">
          <BarChart :chart-data-prop="nonConformitiesPerMachine" :options="chartOptions" />
        </div>
      </div>
      <div class="bloco">
        <h3 class="card-title">Inspeções por operador</h3>
        <div v-if="isLoading" class="loading"><div></div></div>
        <div v-else id="chart-wrapper">
          <BarChart :chart-data-prop="inspectionsPerOperator" :options="chartOptions" />
        </div>
      </div>
      <div class="bloco">
        <h3 class="card-title">Inconformidades por operador</h3>
        <div v-if="isLoading" class="loading"><div></div></div>
        <div v-else id="chart-wrapper">
          <BarChart :chart-data-prop="nonConformitiesPerOperator" :options="chartOptions" />
        </div>
      </div>
    </div>

    <div class="margem container grid-2" v-if="mode === 'table'">
      <div class="bloco">
        <h4 class="card-title">Inspeções por máquina</h4>
        <table class="table-data">
          <thead>
            <tr>
              <th>Máquina</th>
              <th>Qtd. Inspeções</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in summary.inspectionsPerMachine" :key="item.categoria">
              <td>{{ item.categoria }}</td>
              <td>{{ item.quantidade }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bloco">
        <h4 class="card-title">Inconformidades por máquina</h4>
        <table class="table-data">
          <thead>
            <tr>
              <th>Máquina</th>
              <th>Qtd. Inconformidades</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in summary.nonConformitiesPerMachine" :key="item.categoria">
              <td>{{ item.categoria }}</td>
              <td>{{ item.quantidade }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bloco">
        <h4 class="card-title">Inspeções por operador</h4>
        <table class="table-data">
          <thead>
            <tr>
              <th>Operador</th>
              <th>Qtd. Inspeções</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in summary.inspectionsPerOperator" :key="item.categoria">
              <td>{{ item.categoria }}</td>
              <td>{{ item.quantidade }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bloco">
        <h4 class="card-title">Inconformidades por operador</h4>
        <table class="table-data">
          <thead>
            <tr>
              <th>Operador</th>
              <th>Qtd. Inconformidades</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in summary.nonConformitiesPerOperator" :key="item.categoria">
              <td>{{ item.categoria }}</td>
              <td>{{ item.quantidade }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import BarChart from "@/components/BarChart.vue";
import InspectionDataSource from "@/data/datasource/inspection/InspectionDataSource";
import { stringToEndLocalDate, stringToStartLocalDate } from "@/utils/date.util";

export default {
  data() {
    return {
      startDt: new Date().toISOString().split("T")[0],
      endDt: new Date().toISOString().split("T")[0],
      isLoading: true,
      inspections: [],
      mode: "chart",
      report: {
        totalInspections: 0,
        totalNonConformities: 0,
      },
      summary: {
        inspectionsPerMachine: [],
        nonConformitiesPerMachine: [],
        inspectionsPerOperator: [],
        nonConformitiesPerOperator: [],
      },
      sectors: [],
      inspectionsPerMachine: {},
      nonConformitiesPerMachine: {},
      inspectionsPerOperator: {},
      nonConformitiesPerOperator: {},
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            color: "black",
            font: { weight: "bold", size: 16 },
          },
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                const index = tooltipItems[0].dataIndex;
                return this.machineNamesFull ? this.machineNamesFull[index] : tooltipItems[0].label;
              },
            },
          },
        },
      },
    };
  },
  async created() {
    await this.search();
    this.filter();
  },
  methods: {
    truncateLabel(label, maxLength = 15) {
      return label.length > maxLength ? label.slice(0, maxLength) + "..." : label;
    },
    formatDate(dt) {
      return new Date(dt).toLocaleDateString();
    },
    changeMode(mode) {
      this.mode = mode;
    },
    async search() {
      try {
        this.inspections = await InspectionDataSource.getInspections(false, this.startDt, this.endDt);
        this.buildGraphs(this.inspections);
      } catch (error) {
        console.error("Error fetching inspection indicators:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async filter() {
      this.isLoading = true;
      this.inspections = await InspectionDataSource.getInspections(false, this.startDate, this.endDate);

      let filteredInspections = this.inspections;

      if (this.startDt) {
        filteredInspections = filteredInspections.filter((i) => new Date(i.doneAt) >= stringToStartLocalDate(this.startDt));
      }

      if (this.endDt) {
        filteredInspections = filteredInspections.filter((i) => new Date(i.doneAt) <= stringToEndLocalDate(this.endDt));
      }

      this.isLoading = false;
      this.buildGraphs(filteredInspections);
    },

    buildGraphs(inspections) {
      const totalInspections = inspections.length;
      const totalNonConformities = inspections.filter((i) => i.status === 0).length;

      const machineInconfs = {};
      const machineTotal = {};
      const operatorInconfs = {};
      const operatorTotal = {};

  
      inspections.forEach((i) => {
        const machine = i.machine?.name ?? "Desconhecida";
        const operador = i.responsible.name ?? "Sem responsável";

        if (i.status === 0) machineInconfs[machine] = (machineInconfs[machine] || 0) + 1;
        machineTotal[machine] = (machineTotal[machine] || 0) + 1;

        if (i.status === 0) operatorInconfs[operador] = (operatorInconfs[operador] || 0) + 1;
        operatorTotal[operador] = (operatorTotal[operador] || 0) + 1;
      });

      // Método helper para ordenar e colocar labels nos dados
      const sortObjectDesc = (obj, labelTransform = (l) => l) => {
        const sorted = Object.entries(obj).sort((a, b) => b[1] - a[1]);
        return {
          labels: sorted.map(([label]) => labelTransform(label)),
          data: sorted.map(([, value]) => value),
          entries: sorted.map(([categoria, quantidade]) => ({ categoria, quantidade })),
        };
      };

      this.report.totalInspections = totalInspections;
      this.report.totalNonConformities = totalNonConformities;

      // Ordenação e tratamento dos dados
      const machinesTotalSorted = sortObjectDesc(machineTotal, (l) => this.truncateLabel(l, 20));
      const machinesInconfsSorted = sortObjectDesc(machineInconfs, (l) => this.truncateLabel(l, 20));
      const operatorsTotalSorted = sortObjectDesc(operatorTotal);
      const operatorsInconfsSorted = sortObjectDesc(operatorInconfs);

      // Dados dos gráficos
      this.inspectionsPerMachine = {
        labels: machinesTotalSorted.labels,
        datasets: [{ label: "Inspeções por máquina", backgroundColor: "#42A5F5", data: machinesTotalSorted.data }],
      };

      this.nonConformitiesPerMachine = {
        labels: machinesInconfsSorted.labels,
        datasets: [{ label: "Inconformidades por máquina", backgroundColor: "#EF5350", data: machinesInconfsSorted.data }],
      };

      this.inspectionsPerOperator = {
        labels: operatorsTotalSorted.labels,
        datasets: [{ label: "Inspeções por operador", backgroundColor: "#26A69A", data: operatorsTotalSorted.data }],
      };

      this.nonConformitiesPerOperator = {
        labels: operatorsInconfsSorted.labels,
        datasets: [{ label: "Inconformidades por operador", backgroundColor: "#AB47BC", data: operatorsInconfsSorted.data }],
      };

      // Sumarios ordenados
      this.summary.inspectionsPerMachine = machinesTotalSorted.entries;
      this.summary.nonConformitiesPerMachine = machinesInconfsSorted.entries;
      this.summary.inspectionsPerOperator = operatorsTotalSorted.entries;
      this.summary.nonConformitiesPerOperator = operatorsInconfsSorted.entries;
    },

  },
  components: {
    BarChart,
  },
};
</script>

<style scoped>
section {
  margin-bottom: 24px;
}

.search-btn {
  display: flex;
  justify-self: end;
}

.toggle-mode-container {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  padding: 12px 0;
}

.toggle-mode-container button {
  padding-top: 5px;
  padding-bottom: 5px;
}

.card-title {
  padding: 12px 0;
  text-align: center;
}

.date-line {
  justify-content: space-between;
}

.bloco {
  padding: 12px;
}

input {
  max-width: 200px;
}

#chart-wrapper {
  height: 320px;
}

.table-data {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.table-data th,
.table-data td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: center;
}

.table-data th {
  background-color: #f4f4f4;
}
</style>
